import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  createProposal, 
  updateProposal, 
  deleteProposalItem,
  updateProposalDisplaySettings,
  approveProposalOwner,
  releaseProposal,
  manualApproveProposal,
  fetchProposals,
  createCostItem,
  updateReleasedProposal
} from '../../../store/slices/proposalsSlice';
import { showToast } from '../../utils/Toast';

// Utility function to format currency with commas
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '0.00';
  }
  // Ensure we have a clean number, remove any existing commas or dollar signs
  const cleanAmount = typeof amount === 'string' ? 
    parseFloat(amount.replace(/[$,]/g, '')) : 
    Number(amount);
  
  if (isNaN(cleanAmount)) {
    return '0.00';
  }
  
  return cleanAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/**
 * Custom hook to provide proposal-related handler functions
 */
export const useProposalHandlers = ({
  lead,
  formData,
  setFormData,
  currentProposal,
  setCurrentProposal,
  displaySettings,
  setDisplaySettings,
  setShowAddScope,
  setShowAddCostCode,
  onClose,
  calculateProposalTotals,
  createInitialDraftProposal
}) => {
  const dispatch = useDispatch();
  const costCodesData = useSelector(state => state.proposals.costCodesData);

  const handleItemChange = useCallback((index, field, value) => {
    const updatedItems = [...formData.items];
    const currentItem = updatedItems[index];
    
    // Set default values for new items
    if (!currentItem.markup && field === 'item' && currentItem.markup === 0) {
      currentItem.markup = 49.25;
    }
    
    if (!currentItem.margin && field === 'item' && currentItem.margin === 0) {
      currentItem.margin = 33;
    }
    
    // Handle numeric fields
    if (['quantity', 'unitCost', 'builderCost', 'clientPrice', 'markup', 'margin'].includes(field)) {
      currentItem[field] = Number(value) || 0;
    } else {
      currentItem[field] = value;
    }

    // If this is a scope item with cost codes, update the cost code calculations
    if (currentItem.type === 'scope' && currentItem.costCodes && currentItem.costCodes.length > 0) {
      // Update calculations for each cost code
      currentItem.costCodes = currentItem.costCodes.map(costCode => {
        const updatedCostCode = { ...costCode };
        
        // Calculate builder cost
        const quantity = Number(updatedCostCode.quantity) || 0;
        const unitCost = Number(updatedCostCode.unitCost) || 0;
        updatedCostCode.builderCost = quantity * unitCost;
        
        // Calculate client price based on markup
        const markup = Number(updatedCostCode.markup) || 49.25;
        if (updatedCostCode.builderCost > 0) {
          updatedCostCode.clientPrice = updatedCostCode.builderCost * (1 + (markup / 100));
          
          // Calculate margin
          if (updatedCostCode.clientPrice > 0) {
            updatedCostCode.margin = ((updatedCostCode.clientPrice - updatedCostCode.builderCost) / updatedCostCode.clientPrice * 100);
          }
        }
        
        return updatedCostCode;
      });
      
      // Calculate totals for the scope item
      const scopeTotals = currentItem.costCodes.reduce((totals, costCode) => {
        totals.builderCost += Number(costCode.builderCost) || 0;
        totals.clientPrice += Number(costCode.clientPrice) || 0;
        return totals;
      }, { builderCost: 0, clientPrice: 0 });
      
      // Update scope item totals
      currentItem.builderCost = scopeTotals.builderCost;
      currentItem.clientPrice = scopeTotals.clientPrice;
      if (currentItem.clientPrice > 0) {
        currentItem.margin = ((currentItem.clientPrice - currentItem.builderCost) / currentItem.clientPrice * 100);
        currentItem.markup = ((currentItem.clientPrice / currentItem.builderCost - 1) * 100);
      }
    } else {
      // Handle calculations for regular items and cost codes
      if (['quantity', 'unitCost'].includes(field)) {
        const quantity = Number(currentItem.quantity) || 0;
        const unitCost = Number(currentItem.unitCost) || 0;
        
        // Calculate builder cost
        currentItem.builderCost = quantity * unitCost;
        
        // Use existing markup or default to 49.25%
        const markup = Number(currentItem.markup || 49.25);
        
        // Calculate client price based on markup percentage
        if (currentItem.builderCost > 0) {
          // Client price = builder cost * (1 + markup/100)
          currentItem.clientPrice = Number((currentItem.builderCost * (1 + markup/100)).toFixed(2));
          
          // Update margin based on new client price
          if (currentItem.clientPrice > 0) {
            currentItem.margin = Number(((currentItem.clientPrice - currentItem.builderCost) / currentItem.clientPrice * 100).toFixed(2));
          }
        }
      } else if (field === 'markup') {
        const builderCost = Number(currentItem.builderCost) || 0;
        const markup = Number(currentItem.markup) || 0;
        
        if (builderCost) {
          currentItem.clientPrice = builderCost * (1 + (markup / 100));
          
          // Update margin
          if (currentItem.clientPrice) {
            currentItem.margin = Number(((currentItem.clientPrice - builderCost) / currentItem.clientPrice * 100).toFixed(2));
          }
        }
      } else if (field === 'clientPrice') {
        const builderCost = Number(currentItem.builderCost) || 0;
        const clientPrice = Number(currentItem.clientPrice) || 0;
        
        if (builderCost && clientPrice) {
          currentItem.markup = Number(((clientPrice / builderCost - 1) * 100).toFixed(2));
          currentItem.margin = Number(((clientPrice - builderCost) / clientPrice * 100).toFixed(2));
        }
      }
    }

    const totals = calculateProposalTotals(updatedItems);

    setFormData(prev => ({
      ...prev,
      items: updatedItems,
      totalClientPrice: totals.totalClientPrice,
      totalBuilderCost: totals.totalBuilderCost,
      estimatedProfit: totals.estimatedProfit
    }));
  }, [formData, setFormData, calculateProposalTotals]);

  const handleAddScopeOfWork = useCallback(async () => {
    if (!formData.category) {
      showToast('Please select a category first', 'error');
      return;
    }
    const proposal = await createInitialDraftProposal();
    if (proposal) {
      setShowAddScope(true);
    }
  }, [formData.category, createInitialDraftProposal, setShowAddScope]);

  const handleAddCostCodes = useCallback(async () => {
    if (!formData.category) {
      showToast('Please select a category first', 'error');
      return;
    }
    const proposal = await createInitialDraftProposal();
    if (proposal) {
      setShowAddCostCode(true);
    }
  }, [formData.category, createInitialDraftProposal, setShowAddCostCode]);

  const handleSaveCostGroup = useCallback(async (costGroup) => {
    try {
      if (!formData.category) {
        showToast('Please select a category first', 'error');
        return;
      }

      // Store the selected cost codes if they exist
      const selectedCostCodes = costGroup.selectedCostCodes || [];
      
      // Ensure we have a proposal to work with
      let currentProposalToUse = currentProposal;
      
      if (!currentProposalToUse) {
        // Create initial proposal if none exists
        currentProposalToUse = await createInitialDraftProposal();
        if (!currentProposalToUse) {
          return; // createInitialDraftProposal already shows error message
        }
      }
      
      // Create the scope item for the worksheet
      const newScopeItem = {
        type: 'scope',
        _id: costGroup._id,
        costGroup: costGroup,
        item: costGroup.name || 'Untitled Scope',
        description: costGroup.description || '',
        builderCost: Number(costGroup.builderCost) || 0,
        markup: Number(costGroup.markup) || 49.25,
        clientPrice: Number(costGroup.clientPrice) || 0,
        margin: Number(costGroup.margin) || 33,
        costCodes: []
      };
      
              // If there are selected cost codes, associate them with this group
        if (selectedCostCodes.length > 0) {
          // Handle selected cost codes here
          const costCodePromises = selectedCostCodes.map(async (costCode) => {
            return await dispatch(createCostItem({
              item: costCode.item,
              description: costCode.description || '',
              quantity: costCode.quantity || 1,
              unit: costCode.unit || 'EA',
              unitCost: costCode.unitCost || 0,
              builderCost: costCode.builderCost || 0,
              markup: costCode.markup || 49.25,
              clientPrice: costCode.clientPrice || 0,
              margin: costCode.margin || 33,
              proposal: currentProposalToUse._id,
              costGroup: costGroup._id,
              isPartOfGroup: true
            })).unwrap();
          });
        
        const createdCostCodes = await Promise.all(costCodePromises);
        
        // Add the created cost codes to the scope item
        newScopeItem.costCodes = createdCostCodes.map(result => ({
          _id: result.costItem._id,
          item: result.costItem.item,
          description: result.costItem.description || '',
          quantity: result.costItem.quantity || 1,
          unit: result.costItem.unit || 'EA',
          unitCost: result.costItem.unitCost || 0,
          builderCost: result.costItem.builderCost || 0,
          markup: result.costItem.markup || 49.25,
          clientPrice: result.costItem.clientPrice || 0,
          margin: result.costItem.margin || 33
        }));
        
        // Calculate scope totals based on cost codes
        const scopeTotals = newScopeItem.costCodes.reduce((totals, code) => {
          totals.builderCost += Number(code.builderCost) || 0;
          totals.clientPrice += Number(code.clientPrice) || 0;
          return totals;
        }, { builderCost: 0, clientPrice: 0 });
        
        newScopeItem.builderCost = scopeTotals.builderCost;
        newScopeItem.clientPrice = scopeTotals.clientPrice;
        if (scopeTotals.clientPrice > 0) {
          newScopeItem.margin = ((scopeTotals.clientPrice - scopeTotals.builderCost) / scopeTotals.clientPrice) * 100;
        }
      }
      
      // Update the formData with the new scope item
      setFormData(prev => {
        const updatedItems = [...prev.items, newScopeItem];
        const totals = calculateProposalTotals(updatedItems);
        
        return {
          ...prev,
          items: updatedItems,
          totalClientPrice: totals.totalClientPrice,
          totalBuilderCost: totals.totalBuilderCost,
          estimatedProfit: totals.estimatedProfit
        };
      });
      
      setShowAddScope(false);
      return;
    } catch (error) {
      console.error('Failed to create scope of work:', error);
      showToast('Failed to create scope of work: ' + (error.message || 'Unknown error'), 'error');
    }
  }, [formData, currentProposal, lead, dispatch, setCurrentProposal, setFormData, setShowAddScope, calculateProposalTotals, costCodesData]);

  const handleSaveCostItem = useCallback(async (costItemData) => {
    try {
      const result = await dispatch(createCostItem(costItemData)).unwrap();
      
      if (result.costItem) {
        // Update the worksheet with the new cost item
        setFormData(prev => {
          const newItem = {
            type: 'cost',
            _id: result.costItem._id,
            costItem: result.costItem,
            item: result.costItem.item,
            description: result.costItem.description,
            quantity: result.costItem.quantity,
            unit: result.costItem.unit !== undefined && result.costItem.unit !== '' ? result.costItem.unit : 'EA',
            unitCost: result.costItem.unitCost,
            builderCost: result.costItem.builderCost,
            markup: result.costItem.markup,
            clientPrice: result.costItem.clientPrice,
            margin: result.costItem.margin
          };

          const updatedItems = [...prev.items, newItem];
          const totals = calculateProposalTotals(updatedItems);

          return {
            ...prev,
            items: updatedItems,
            totalClientPrice: totals.totalClientPrice,
            totalBuilderCost: totals.totalBuilderCost,
            estimatedProfit: totals.estimatedProfit
          };
        });
      }
    } catch (error) {
      console.error('Error saving cost item:', error);
      showToast('Failed to save cost item', 'error');
    }
  }, [dispatch, setFormData, calculateProposalTotals]);

  const handleDeleteItem = useCallback(async (indexToDelete) => {
    try {
      const itemToDelete = formData.items[indexToDelete];
      
      if (currentProposal?._id) {
        // If we have a saved proposal, delete from the server
        if (itemToDelete.type === 'scope') {
          // For scope items (cost groups), we need to delete the group and all its associated items
          await dispatch(deleteProposalItem({
            proposalId: currentProposal._id,
            itemId: itemToDelete._id || itemToDelete.costGroup?._id,
            itemType: 'scope',
            deleteAssociatedItems: true // This flag tells the backend to delete associated cost items
          })).unwrap();

          // Also remove any cost items that were part of this group from the local state
          setFormData(prev => {
            const updatedItems = prev.items.filter((item, index) => {
              if (index === indexToDelete) return false; // Remove the scope item itself
              if (item.type === 'cost' && item.costGroup === itemToDelete._id) return false; // Remove associated cost items
              return true;
            });
            const totals = calculateProposalTotals(updatedItems);
            
            return {
              ...prev,
              items: updatedItems,
              totalClientPrice: totals.totalClientPrice,
              totalBuilderCost: totals.totalBuilderCost,
              estimatedProfit: totals.estimatedProfit
            };
          });
        } else {
          // For individual cost items
          await dispatch(deleteProposalItem({
            proposalId: currentProposal._id,
            itemId: itemToDelete._id || itemToDelete.costItem?._id,
            itemType: 'cost',
            costGroupId: itemToDelete.costGroup || null // Pass the group ID if it's a grouped item
          })).unwrap();

          // Update local state by removing just this item
          setFormData(prev => {
            const updatedItems = prev.items.filter((_, index) => index !== indexToDelete);
            const totals = calculateProposalTotals(updatedItems);
            
            return {
              ...prev,
              items: updatedItems,
              totalClientPrice: totals.totalClientPrice,
              totalBuilderCost: totals.totalBuilderCost,
              estimatedProfit: totals.estimatedProfit
            };
          });
        }

        showToast('Item deleted successfully', 'success');
      } else {
        // If no saved proposal, just update local state
        setFormData(prev => {
          let updatedItems;
          if (itemToDelete.type === 'scope') {
            // Remove the scope item and its associated cost items
            updatedItems = prev.items.filter((item, index) => {
              if (index === indexToDelete) return false; // Remove the scope item itself
              if (item.type === 'cost' && item.costGroup === itemToDelete._id) return false; // Remove associated cost items
              return true;
            });
          } else {
            // Remove just the single item
            updatedItems = prev.items.filter((_, index) => index !== indexToDelete);
          }

          const totals = calculateProposalTotals(updatedItems);
          
          return {
            ...prev,
            items: updatedItems,
            totalClientPrice: totals.totalClientPrice,
            totalBuilderCost: totals.totalBuilderCost,
            estimatedProfit: totals.estimatedProfit
          };
        });
      }
    } catch (error) {
      console.error('Failed to delete item:', error);
      showToast('Failed to delete item: ' + (error.message || 'Unknown error'), 'error');
    }
  }, [formData.items, currentProposal, dispatch, setFormData, calculateProposalTotals]);

  const handleSaveAsDraft = useCallback(async () => {
    try {
      // Transform items to proper format for API
      const processedItems = formData.items.map(item => {
        const baseItem = {
          ...item,
          quantity: Number(item.quantity) || 1,
          unit: item.unit !== undefined && item.unit !== '' ? item.unit : 'EA',
          unitCost: Number(item.unitCost) || 0,
          builderCost: Number(item.builderCost) || 0,
          markup: Number(item.markup) || 49.25,
          clientPrice: Number(item.clientPrice) || 0,
          margin: Number(item.margin) || 33
        };

        if (item.type === 'scope' && item.costCodes) {
          // Process cost codes within scope items
          baseItem.costCodes = item.costCodes.map(code => ({
            ...code,
            quantity: Number(code.quantity) || 1,
            unit: code.unit !== undefined && code.unit !== '' ? code.unit : 'EA',
            unitCost: Number(code.unitCost) || 0,
            builderCost: Number(code.builderCost) || 0,
            markup: Number(code.markup) || 49.25,
            clientPrice: Number(code.clientPrice) || 0,
            margin: Number(code.margin) || 33
          }));
        }

        return baseItem;
      });

      const proposalData = {
        title: formData.title || `Proposal for ${lead.client?.name || lead.leadContact?.name || 'Client'}`,
        category: formData.category,
        emailAddress: lead.primaryEmail || lead.client?.email || lead.leadContact?.email,
        status: 'draft',
        scopeOfWork: processedItems
          .filter(item => item.type === 'scope')
          .map(item => ({
            costGroup: item.costGroup?._id ? {
              _id: item.costGroup._id,
              name: item.item || item.costGroup.name,
              description: item.description || item.costGroup.description || '',
              builderCost: Number(item.builderCost) || 0,
              markup: Number(item.markup) || 49.25,
              clientPrice: Number(item.clientPrice) || 0,
              margin: Number(item.margin) || 33,
              costItems: item.costCodes?.map(code => code._id).filter(Boolean) || []
            } : {
              name: item.item || 'Untitled Item',
              description: item.description || '',
              builderCost: Number(item.builderCost) || 0,
              markup: Number(item.markup) || 49.25,
              clientPrice: Number(item.clientPrice) || 0,
              margin: Number(item.margin) || 33
            },
            quantity: Number(item.quantity) || 1,
            unit: item.unit !== undefined && item.unit !== '' ? item.unit : 'EA'
          })),
        costCodes: [
          // First add standalone cost items
          ...processedItems
            .filter(item => item.type === 'cost')
            .map(item => {
              const baseCostCode = {
                quantity: Number(item.quantity) || 1,
                unit: item.unit !== undefined && item.unit !== '' ? item.unit : 'EA',
                unitCost: Number(item.unitCost) || 0,
                builderCost: Number(item.builderCost) || 0,
                markup: Number(item.markup) || 49.25,
                clientPrice: Number(item.clientPrice) || 0,
                margin: Number(item.margin) || 33,
                costGroup: item.costGroup || null
              };

              // With costItem reference
              if (item.costItem?._id) {
                return {
                  costItem: {
                    _id: item.costItem._id,
                    item: item.item || item.costItem.item,
                    description: item.description || item.costItem.description || '',
                    ...baseCostCode
                  },
                  ...baseCostCode
                };
              }

              // New cost item
              return {
                costItem: {
                  item: item.item || 'Untitled Item',
                  description: item.description || '',
                  ...baseCostCode
                },
                ...baseCostCode
              };
            }),
          // Then add cost codes from scope items - FIXED: handle both existing and new cost codes
          ...processedItems
            .filter(item => item.type === 'scope' && item.costCodes?.length > 0)
            .flatMap(item => 
              item.costCodes.map(code => {
                const baseCostCode = {
                  quantity: Number(code.quantity) || 1,
                  unit: code.unit !== undefined && code.unit !== '' ? code.unit : 'EA',
                  unitCost: Number(code.unitCost) || 0,
                  builderCost: Number(code.builderCost) || 0,
                  markup: Number(code.markup) || 49.25,
                  clientPrice: Number(code.clientPrice) || 0,
                  margin: Number(code.margin) || 33,
                  costGroup: item.costGroup?._id || null
                };

                // If the cost code already has an _id (existing cost item)
                if (code._id) {
                  return {
                    costItem: {
                      _id: code._id,
                      item: code.item,
                      description: code.description || '',
                      ...baseCostCode
                    },
                    ...baseCostCode
                  };
                }

                // For new cost codes without an _id
                return {
                  costItem: {
                    item: code.item || 'Untitled Item',
                    description: code.description || '',
                    ...baseCostCode,
                    proposal: currentProposal?._id,
                    costGroup: item.costGroup?._id || null,
                    isPartOfGroup: !!item.costGroup?._id
                  },
                  ...baseCostCode
                };
              })
            )
        ],
        displaySettings: displaySettings,
        clientPrice: formData.totalClientPrice,
        builderCost: formData.totalBuilderCost,
        estimatedProfit: formData.estimatedProfit,
        topsailRepInitials: formData.topsailRepInitials || '',
        contractorName: formData.contractorName || '',
        contractorSignature: formData.contractorSignature || '',
        agreementDetails: formData.agreementDetails || {}
      };

      if (currentProposal?._id) {
        // Update existing proposal
        await dispatch(updateProposal({
          id: currentProposal._id,
          updates: proposalData
        })).unwrap();
      } else {
        // Create new proposal
        const result = await dispatch(createProposal({
          leadId: lead._id,
          proposalData
        })).unwrap();
        setCurrentProposal(result.proposal);
      }

      // Refresh the proposal data
      if (lead?._id) {
        await dispatch(fetchProposals(lead._id));
      }

      showToast('Proposal saved as draft successfully', 'success');
    } catch (error) {
      console.error('Error saving draft:', error);
      showToast('Failed to save draft', 'error');
    }
  }, [formData, displaySettings, currentProposal, lead, dispatch, setCurrentProposal]);

  const handleSaveAndApprove = useCallback(async () => {
    try {
      if (!formData.category) {
        showToast('Please select a category', 'error');
        return;
      }

      // First create/update the proposal as draft
      const proposalData = {
        title: formData.title || `Proposal for ${lead.client?.name || lead.leadContact?.name || 'Client'}`,
        emailAddress: lead.clientEmail || lead.client?.email || '',
        status: 'draft',
        category: formData.category,
        clientPrice: formData.totalClientPrice || 0,
        topsailRepInitials: formData.topsailRepInitials || '',
        contractorName: formData.contractorName || '',
        contractorSignature: formData.contractorSignature || '',
        agreementDetails: formData.agreementDetails || {},
        displaySettings: displaySettings,
        
        // Transform scope of work items while preserving costGroup data
        scopeOfWork: formData.items
          .filter(item => item.type === 'scope')
          .map(item => {
            // If the item already has a costGroup with an ID, use that
            if (item.costGroup?._id) {
              return {
                costGroup: item.costGroup._id,
                quantity: item.quantity || 1,
                unit: item.unit !== undefined && item.unit !== '' ? item.unit : 'EA',
                unitCost: item.unitCost || 0,
                builderCost: item.builderCost || 0,
                markup: item.markup || 0,
                clientPrice: item.clientPrice || 0,
                margin: item.margin || 0
              };
            }

            // For new items, create a new costGroup
            return {
              costGroup: {
                name: item.item || 'Untitled Item', // Ensure name is always provided
                description: item.description || '',
                builderCost: item.builderCost || 0,
                markup: item.markup || 0,
                clientPrice: item.clientPrice || 0,
                margin: item.margin || 0,
                proposal: currentProposal?._id // Link to current proposal
              },
              quantity: item.quantity || 1,
              unit: item.unit !== undefined && item.unit !== '' ? item.unit : 'EA',
              unitCost: item.unitCost || 0,
              builderCost: item.builderCost || 0,
              markup: item.markup || 0,
              clientPrice: item.clientPrice || 0,
              margin: item.margin || 0
            };
          }),

        // Transform cost code items while preserving costItem data
        costCodes: formData.items
          .filter(item => item.type === 'cost')
          .map(item => {
            // If the item already has a costItem with an ID, use that
            if (item.costItem?._id) {
              return {
                costItem: item.costItem._id,
                quantity: item.quantity || 1,
                unit: item.unit !== undefined && item.unit !== '' ? item.unit : 'EA',
                unitCost: item.unitCost || 0,
                builderCost: item.builderCost || 0,
                markup: item.markup || 0,
                clientPrice: item.clientPrice || 0,
                margin: item.margin || 0
              };
            }

            // For new items, create a new costItem
            return {
              costItem: {
                item: item.item || 'Untitled Item', // Ensure item name is always provided
                description: item.description || '',
                unit: item.unit !== undefined && item.unit !== '' ? item.unit : 'EA',
                quantity: item.quantity || 1,
                unitCost: item.unitCost || 0,
                builderCost: item.builderCost || 0,
                markup: item.markup || 0,
                clientPrice: item.clientPrice || 0,
                margin: item.margin || 0
              },
              quantity: item.quantity || 1,
              unit: item.unit !== undefined && item.unit !== '' ? item.unit : 'EA',
              unitCost: item.unitCost || 0,
              builderCost: item.builderCost || 0,
              markup: item.markup || 0,
              clientPrice: item.clientPrice || 0,
              margin: item.margin || 0
            };
          })
      };

      let proposalId;
      if (currentProposal?._id) {
        // Update existing proposal
        const result = await dispatch(updateProposal({
          id: currentProposal._id,
          updates: proposalData
        })).unwrap();
        proposalId = result.proposal._id;
      } else {
        // Create new proposal
        const result = await dispatch(createProposal({
          leadId: lead._id,
          proposalData
        })).unwrap();
        proposalId = result.proposal._id;
      }

      // Then approve it
      await dispatch(approveProposalOwner(proposalId)).unwrap();
      
      // Refresh proposals list and close dialog
      await dispatch(fetchProposals(lead._id));
      onClose();
    } catch (error) {
      console.error('Failed to save and approve proposal:', error);
      showToast('Failed to save and approve proposal: ' + (error.message || 'Unknown error'), 'error');
    }
  }, [formData, displaySettings, currentProposal, lead, dispatch, onClose]);

  const handleSaveAndRelease = useCallback(async () => {
    try {
      if (!formData.category) {
        showToast('Please select a category', 'error');
        return;
      }

      // First create/update the proposal as draft
      const proposalData = {
        title: formData.title || `Proposal for ${lead.client?.name || lead.leadContact?.name || 'Client'}`,
        emailAddress: lead.clientEmail || lead.client?.email || '',
        status: 'draft',
        category: formData.category,
        clientPrice: formData.totalClientPrice || 0,
        topsailRepInitials: formData.topsailRepInitials || '',
        contractorName: formData.contractorName || '',
        contractorSignature: formData.contractorSignature || '',
        agreementDetails: formData.agreementDetails || {},
        displaySettings: displaySettings,
        scopeOfWork: formData.items
          .filter(item => item.type === 'scope')
          .map(item => {
            // If the item already has a costGroup with an ID, use that
            if (item.costGroup?._id) {
              // Get associated cost codes for this scope item
              const associatedCostCodes = item.costCodes?.map(code => code._id) || [];
              
              return {
                costGroup: item.costGroup._id,
                quantity: item.quantity || 1,
                unit: item.unit || 'EA',
                unitCost: Number(item.unitCost) || 0,
                builderCost: Number(item.builderCost) || 0,
                markup: Number(item.markup) || 49.25,
                clientPrice: Number(item.clientPrice) || 0,
                margin: Number(item.margin) || 33,
                costItems: associatedCostCodes // Include associated cost codes
              };
            }

            // For new items, create a new costGroup
            const associatedCostCodes = item.costCodes?.map(code => code._id) || [];
            
            return {
              costGroup: {
                name: item.item || 'Untitled Item',
                description: item.description || '',
                builderCost: Number(item.builderCost) || 0,
                markup: Number(item.markup) || 49.25,
                clientPrice: Number(item.clientPrice) || 0,
                margin: Number(item.margin) || 33,
                proposal: currentProposal?._id,
                costItems: associatedCostCodes // Include associated cost codes
              },
              quantity: item.quantity || 1,
              unit: item.unit || 'EA',
              unitCost: Number(item.unitCost) || 0,
              builderCost: Number(item.builderCost) || 0,
              markup: Number(item.markup) || 49.25,
              clientPrice: Number(item.clientPrice) || 0,
              margin: Number(item.margin) || 33
            };
          }),
        costCodes: [
          // Standalone cost codes
          ...formData.items
            .filter(item => item.type === 'cost')
            .map(item => {
              // If the item already has a costItem with an ID, use that
              if (item.costItem?._id) {
                return {
                  costItem: item.costItem._id,
                  quantity: item.quantity || 1,
                  unit: item.unit || 'EA',
                  unitCost: Number(item.unitCost) || 0,
                  builderCost: Number(item.builderCost) || 0,
                  markup: Number(item.markup) || 49.25,
                  clientPrice: Number(item.clientPrice) || 0,
                  margin: Number(item.margin) || 33,
                  costGroup: item.costGroup || null,
                  isPartOfGroup: !!item.costGroup
                };
              }

              // For new items, create a new costItem
              return {
                costItem: {
                  item: item.item || 'Untitled Item',
                  description: item.description || '',
                  unit: item.unit || 'EA',
                  quantity: item.quantity || 1,
                  unitCost: Number(item.unitCost) || 0,
                  builderCost: Number(item.builderCost) || 0,
                  markup: Number(item.markup) || 49.25,
                  clientPrice: Number(item.clientPrice) || 0,
                  margin: Number(item.margin) || 33,
                  proposal: currentProposal?._id,
                  costGroup: item.costGroup || null,
                  isPartOfGroup: !!item.costGroup
                },
                quantity: item.quantity || 1,
                unit: item.unit || 'EA',
                unitCost: Number(item.unitCost) || 0,
                builderCost: Number(item.builderCost) || 0,
                markup: Number(item.markup) || 49.25,
                clientPrice: Number(item.clientPrice) || 0,
                margin: Number(item.margin) || 33
              };
            }),
          
          // Cost codes that are part of scope items
          ...formData.items
            .filter(item => item.type === 'scope' && item.costCodes && item.costCodes.length > 0)
            .flatMap(scopeItem => 
              scopeItem.costCodes.map(code => {
                // Only include the cost code if it has an _id (existing item)
                if (code._id) {
                  return {
                    costItem: {
                      _id: code._id,
                      item: code.item || 'Untitled Item',
                      description: code.description || '',
                      unit: code.unit || 'EA',
                      quantity: Number(code.quantity) || 1,
                      unitCost: Number(code.unitCost) || 0,
                      builderCost: Number(code.builderCost) || 0,
                      markup: Number(code.markup) || 49.25,
                      clientPrice: Number(code.clientPrice) || 0,
                      margin: Number(code.margin) || 33
                    },
                    costGroup: scopeItem.costGroup?._id || scopeItem._id,
                    isPartOfGroup: true,
                    quantity: Number(code.quantity) || 1,
                    unit: code.unit || 'EA',
                    unitCost: Number(code.unitCost) || 0,
                    builderCost: Number(code.builderCost) || 0,
                    markup: Number(code.markup) || 49.25,
                    clientPrice: Number(code.clientPrice) || 0,
                    margin: Number(code.margin) || 33
                  };
                }
                // For new cost codes without an ID
                return {
                  costItem: {
                    item: code.item || 'Untitled Item',
                    description: code.description || '',
                    unit: code.unit || 'EA',
                    quantity: Number(code.quantity) || 1,
                    unitCost: Number(code.unitCost) || 0,
                    builderCost: Number(code.builderCost) || 0,
                    markup: Number(code.markup) || 49.25,
                    clientPrice: Number(code.clientPrice) || 0,
                    margin: Number(code.margin) || 33,
                    proposal: currentProposal?._id
                  },
                  costGroup: scopeItem.costGroup?._id || scopeItem._id,
                  isPartOfGroup: true,
                  quantity: Number(code.quantity) || 1,
                  unit: code.unit || 'EA',
                  unitCost: Number(code.unitCost) || 0,
                  builderCost: Number(code.builderCost) || 0,
                  markup: Number(code.markup) || 49.25,
                  clientPrice: Number(code.clientPrice) || 0,
                  margin: Number(code.margin) || 33
                };
              })
            )
        ]
      };

      let proposalId;
      if (currentProposal?._id) {
        // Update existing proposal
        const result = await dispatch(updateProposal({
          id: currentProposal._id,
          updates: proposalData
        })).unwrap();
        proposalId = result.proposal._id;
      } else {
        // Create new proposal
        const result = await dispatch(createProposal({
          leadId: lead._id,
          proposalData
        })).unwrap();
        proposalId = result.proposal._id;
      }

      // Then release it
      await dispatch(releaseProposal(proposalId)).unwrap();
      
      // Refresh proposals list and close dialog
      await dispatch(fetchProposals(lead._id));
      onClose();
    } catch (error) {
      console.error('Failed to save and release proposal:', error);
      showToast('Failed to save and release proposal: ' + (error.message || 'Unknown error'), 'error');
    }
  }, [formData, displaySettings, currentProposal, lead, dispatch, onClose]);

  const handleSaveAndManualApprove = useCallback(async () => {
    try {
      if (!formData.category) {
        showToast('Please select a category', 'error');
        return;
      }

      // First create/update the proposal as draft
      const proposalData = {
        title: formData.title || `Proposal for ${lead.client?.name || lead.leadContact?.name || 'Client'}`,
        emailAddress: lead.clientEmail || lead.client?.email || '',
        status: 'draft',
        category: formData.category,
        clientPrice: formData.totalClientPrice || 0,
        topsailRepInitials: formData.topsailRepInitials || '',
        contractorName: formData.contractorName || '',
        contractorSignature: formData.contractorSignature || '',
        agreementDetails: formData.agreementDetails || {},
        displaySettings: displaySettings,
        scopeOfWork: formData.items
          .filter(item => item.type === 'scope')
          .map(item => {
            // If the item already has a costGroup with an ID, use that
            if (item.costGroup?._id) {
              // Get associated cost codes for this scope item
              const associatedCostCodes = item.costCodes?.map(code => code._id) || [];
              
              return {
                costGroup: item.costGroup._id,
                quantity: item.quantity || 1,
                unit: item.unit || 'EA',
                unitCost: Number(item.unitCost) || 0,
                builderCost: Number(item.builderCost) || 0,
                markup: Number(item.markup) || 49.25,
                clientPrice: Number(item.clientPrice) || 0,
                margin: Number(item.margin) || 33,
                costItems: associatedCostCodes // Include associated cost codes
              };
            }

            // For new items, create a new costGroup
            const associatedCostCodes = item.costCodes?.map(code => code._id) || [];
            
            return {
              costGroup: {
                name: item.item || 'Untitled Item',
                description: item.description || '',
                builderCost: Number(item.builderCost) || 0,
                markup: Number(item.markup) || 49.25,
                clientPrice: Number(item.clientPrice) || 0,
                margin: Number(item.margin) || 33,
                proposal: currentProposal?._id,
                costItems: associatedCostCodes // Include associated cost codes
              },
              quantity: item.quantity || 1,
              unit: item.unit || 'EA',
              unitCost: Number(item.unitCost) || 0,
              builderCost: Number(item.builderCost) || 0,
              markup: Number(item.markup) || 49.25,
              clientPrice: Number(item.clientPrice) || 0,
              margin: Number(item.margin) || 33
            };
          }),
        costCodes: [
          // Standalone cost codes
          ...formData.items
            .filter(item => item.type === 'cost')
            .map(item => {
              // If the item already has a costItem with an ID, use that
              if (item.costItem?._id) {
                return {
                  costItem: item.costItem._id,
                  quantity: item.quantity || 1,
                  unit: item.unit || 'EA',
                  unitCost: Number(item.unitCost) || 0,
                  builderCost: Number(item.builderCost) || 0,
                  markup: Number(item.markup) || 49.25,
                  clientPrice: Number(item.clientPrice) || 0,
                  margin: Number(item.margin) || 33,
                  costGroup: item.costGroup || null,
                  isPartOfGroup: !!item.costGroup
                };
              }

              // For new items, create a new costItem
              return {
                costItem: {
                  item: item.item || 'Untitled Item',
                  description: item.description || '',
                  unit: item.unit || 'EA',
                  quantity: item.quantity || 1,
                  unitCost: Number(item.unitCost) || 0,
                  builderCost: Number(item.builderCost) || 0,
                  markup: Number(item.markup) || 49.25,
                  clientPrice: Number(item.clientPrice) || 0,
                  margin: Number(item.margin) || 33,
                  proposal: currentProposal?._id,
                  costGroup: item.costGroup || null,
                  isPartOfGroup: !!item.costGroup
                },
                quantity: item.quantity || 1,
                unit: item.unit || 'EA',
                unitCost: Number(item.unitCost) || 0,
                builderCost: Number(item.builderCost) || 0,
                markup: Number(item.markup) || 49.25,
                clientPrice: Number(item.clientPrice) || 0,
                margin: Number(item.margin) || 33
              };
            }),
          
          // Cost codes that are part of scope items
          ...formData.items
            .filter(item => item.type === 'scope' && item.costCodes && item.costCodes.length > 0)
            .flatMap(scopeItem => 
              scopeItem.costCodes.map(code => {
                // Only include the cost code if it has an _id (existing item)
                if (code._id) {
                  return {
                    costItem: {
                      _id: code._id,
                      item: code.item || 'Untitled Item',
                      description: code.description || '',
                      unit: code.unit || 'EA',
                      quantity: Number(code.quantity) || 1,
                      unitCost: Number(code.unitCost) || 0,
                      builderCost: Number(code.builderCost) || 0,
                      markup: Number(code.markup) || 49.25,
                      clientPrice: Number(code.clientPrice) || 0,
                      margin: Number(code.margin) || 33
                    },
                    costGroup: scopeItem.costGroup?._id || scopeItem._id,
                    isPartOfGroup: true,
                    quantity: Number(code.quantity) || 1,
                    unit: code.unit || 'EA',
                    unitCost: Number(code.unitCost) || 0,
                    builderCost: Number(code.builderCost) || 0,
                    markup: Number(code.markup) || 49.25,
                    clientPrice: Number(code.clientPrice) || 0,
                    margin: Number(code.margin) || 33
                  };
                }
                // For new cost codes without an ID
                return {
                  costItem: {
                    item: code.item || 'Untitled Item',
                    description: code.description || '',
                    unit: code.unit || 'EA',
                    quantity: Number(code.quantity) || 1,
                    unitCost: Number(code.unitCost) || 0,
                    builderCost: Number(code.builderCost) || 0,
                    markup: Number(code.markup) || 49.25,
                    clientPrice: Number(code.clientPrice) || 0,
                    margin: Number(code.margin) || 33,
                    proposal: currentProposal?._id
                  },
                  costGroup: scopeItem.costGroup?._id || scopeItem._id,
                  isPartOfGroup: true,
                  quantity: Number(code.quantity) || 1,
                  unit: code.unit || 'EA',
                  unitCost: Number(code.unitCost) || 0,
                  builderCost: Number(code.builderCost) || 0,
                  markup: Number(code.markup) || 49.25,
                  clientPrice: Number(code.clientPrice) || 0,
                  margin: Number(code.margin) || 33
                };
              })
            )
        ]
      };

      let proposalId;
      if (currentProposal?._id) {
        // Update existing proposal
        const result = await dispatch(updateProposal({
          id: currentProposal._id,
          updates: proposalData
        })).unwrap();
        proposalId = result.proposal._id;
      } else {
        // Create new proposal
        const result = await dispatch(createProposal({
          leadId: lead._id,
          proposalData
        })).unwrap();
        proposalId = result.proposal._id;
      }

      // Then manually approve it
      await dispatch(manualApproveProposal(proposalId)).unwrap();
      
      // Refresh proposals list and close dialog
      await dispatch(fetchProposals(lead._id));
      onClose();
    } catch (error) {
      console.error('Failed to save and manually approve proposal:', error);
      showToast('Failed to save and manually approve proposal: ' + (error.message || 'Unknown error'), 'error');
    }
  }, [formData, displaySettings, currentProposal, lead, dispatch, onClose]);

  const handleDisplaySettingChange = useCallback(async (setting) => {
    const newSettings = {
      ...displaySettings,
      [setting]: !displaySettings[setting]
    };
    
    // Update local state immediately
    setDisplaySettings(newSettings);
    
    // Save to backend if we have a current proposal
    if (currentProposal?._id) {
      try {
        await dispatch(updateProposalDisplaySettings({
          id: currentProposal._id,
          displaySettings: newSettings
        })).unwrap();
        
        // Don't refetch proposals - the local state update is sufficient
      } catch (error) {
        console.error('Failed to update display settings:', error);
        showToast('Failed to update display settings', 'error');
        // Revert local state on error
        setDisplaySettings(displaySettings);
      }
    }
  }, [displaySettings, setDisplaySettings, currentProposal, dispatch]);

  const handleUpdateReleasedProposal = useCallback(async () => {
    try {
      if (!currentProposal?._id) {
        showToast('No proposal to update', 'error');
        return;
      }

      // Process scope items and their cost codes
      const processedScopeOfWork = formData.items
        .filter(item => item.type === 'scope')
        .map(item => {
          // Process cost codes first
          const costCodes = (item.costCodes || []).map(code => ({
            _id: code._id,
            item: code.item,
            description: code.description || '',
            quantity: Number(code.quantity) || 1,
            unit: code.unit || 'SQ',
            unitCost: Number(code.unitCost) || 0,
            builderCost: Number(code.builderCost) || 0,
            markup: Number(code.markup) || 49.25,
            clientPrice: Number(code.clientPrice) || 0,
            margin: Number(code.margin) || 33,
            costGroup: item.costGroup?._id,
            isPartOfGroup: true
          }));

          // Calculate scope totals from cost codes
          const scopeTotals = costCodes.reduce((acc, code) => {
            acc.builderCost += Number(code.builderCost) || 0;
            acc.clientPrice += Number(code.clientPrice) || 0;
            return acc;
          }, { builderCost: 0, clientPrice: 0 });

          return {
            costGroup: {
              _id: item.costGroup?._id,
              name: item.item,
              description: item.description || '',
              builderCost: scopeTotals.builderCost,
              markup: Number(item.markup) || 49.25,
              clientPrice: scopeTotals.clientPrice,
              margin: scopeTotals.clientPrice > 0 ? 
                ((scopeTotals.clientPrice - scopeTotals.builderCost) / scopeTotals.clientPrice) * 100 : 33
            },
            costItems: costCodes
          };
        });

      // Process standalone cost codes
      const processedCostCodes = formData.items
        .filter(item => item.type === 'cost')
        .map(item => {
          const builderCost = Number(item.quantity || 1) * Number(item.unitCost || 0);
          const markup = Number(item.markup) || 49.25;
          const clientPrice = builderCost * (1 + (markup / 100));
          const margin = clientPrice > 0 ? ((clientPrice - builderCost) / clientPrice) * 100 : 33;

          return {
            costItem: {
              _id: item.costItem?._id,
              item: item.item,
              description: item.description || '',
              quantity: Number(item.quantity) || 1,
              unit: item.unit || 'EA',
              unitCost: Number(item.unitCost) || 0,
              builderCost: builderCost,
              markup: markup,
              clientPrice: clientPrice,
              margin: margin,
              isPartOfGroup: false,
              costType: item.costItem?.costType || []
            }
          };
        });

      // Calculate overall totals
      const totals = {
        builderCost: 0,
        clientPrice: 0,
        estimatedProfit: 0
      };

      // Add scope totals
      processedScopeOfWork.forEach(scope => {
        totals.builderCost += Number(scope.costGroup.builderCost) || 0;
        totals.clientPrice += Number(scope.costGroup.clientPrice) || 0;
      });

      // Add cost code totals
      processedCostCodes.forEach(code => {
        totals.builderCost += Number(code.costItem.builderCost) || 0;
        totals.clientPrice += Number(code.costItem.clientPrice) || 0;
      });

      totals.estimatedProfit = totals.clientPrice - totals.builderCost;

      const proposalData = {
        title: currentProposal.title,
        emailAddress: currentProposal.emailAddress,
        category: formData.category,
        clientPrice: totals.clientPrice,
        builderCost: totals.builderCost,
        estimatedProfit: totals.estimatedProfit,
        displaySettings,
        proposalId: currentProposal.proposalId,
        scopeOfWork: processedScopeOfWork,
        costCodes: processedCostCodes,
        topsailRepInitials: formData.topsailRepInitials || '',
        contractorName: formData.contractorName || '',
        contractorSignature: formData.contractorSignature || '',
        agreementDetails: formData.agreementDetails || {},
        status: 'released'
      };

      // Update the released proposal
      await dispatch(updateReleasedProposal({
        id: currentProposal._id,
        updates: proposalData
      })).unwrap();

      // Refresh proposals list
      await dispatch(fetchProposals(lead._id));
      
      // Show success message and close dialog
      showToast('Released proposal updated successfully', 'success');
      onClose();
    } catch (error) {
      console.error('Failed to update released proposal:', error);
      showToast('Failed to update released proposal: ' + (error.message || 'Unknown error'), 'error');
    }
  }, [currentProposal, formData, lead, dispatch, displaySettings, onClose]);

  return {
    handleItemChange,
    handleAddScopeOfWork,
    handleAddCostCodes,
    handleSaveCostGroup,
    handleSaveCostItem,
    handleDeleteItem,
    handleSaveAsDraft,
    handleSaveAndApprove,
    handleSaveAndRelease,
    handleSaveAndManualApprove,
    handleDisplaySettingChange,
    handleUpdateReleasedProposal
  };
};

// This mapProposalToWorksheet function was added as a duplicate
// The actual implementation is in CreateProposalDialog.js
// Keeping this comment for reference

/*
const mapProposalToWorksheet = (proposal) => {
  if (!proposal) return null;

  const items = [];

  // Process scope of work items (cost groups)
  if (proposal.scopeOfWork && proposal.scopeOfWork.length > 0) {
    proposal.scopeOfWork.forEach(scopeItem => {
      if (scopeItem.costGroup) {
        const costGroup = scopeItem.costGroup;
        
        // Create scope item from cost group
        const newScopeItem = {
          type: 'scope',
          _id: costGroup._id,
          item: costGroup.name,
          description: costGroup.description,
          builderCost: costGroup.builderCost || 0,
          markup: costGroup.markup || 49.25,
          clientPrice: costGroup.clientPrice || 0,
          margin: costGroup.margin || 33,
          costCodes: []
        };
        
        // Add associated cost items if they exist
        if (scopeItem.costItems && scopeItem.costItems.length > 0) {
          scopeItem.costItems.forEach(costItem => {
            newScopeItem.costCodes.push({
              _id: costItem._id,
              item: costItem.item,
              description: costItem.description || '',
              quantity: costItem.quantity || 1,
              unit: costItem.unit || 'EA',
              unitCost: costItem.unitCost || 0,
              builderCost: costItem.builderCost || 0,
              markup: costItem.markup || 49.25,
              clientPrice: costItem.clientPrice || 0,
              margin: costItem.margin || 33
            });
          });
        }
        
        items.push(newScopeItem);
      }
    });
  }

  // Process individual cost codes (not part of any group)
  if (proposal.costCodes && proposal.costCodes.length > 0) {
    proposal.costCodes.forEach(codeItem => {
      if (codeItem.costItem) {
        const costItem = codeItem.costItem;
        
        items.push({
          type: 'cost',
          _id: costItem._id,
          item: costItem.item,
          description: costItem.description || '',
          quantity: costItem.quantity || 1,
          unit: costItem.unit || 'EA',
          unitCost: costItem.unitCost || 0,
          builderCost: costItem.builderCost || 0,
          markup: costItem.markup || 49.25,
          clientPrice: costItem.clientPrice || 0,
          margin: costItem.margin || 33
        });
      }
    });
  }

  // Calculate totals
  const { totalClientPrice, totalBuilderCost, estimatedProfit } = calculateProposalTotals(items);

  return {
    category: proposal.category ? proposal.category._id : '',
    items,
    totalClientPrice,
    totalBuilderCost,
    estimatedProfit
  };
};
*/ 